import React, { Component } from 'react';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { FormicaProps, withFormica } from '../../helpers/withFormica';
import { RootState } from '../../redux/store';
import Breadcrumb from '../../components/Breadcrumb';
import Icon from '../../icons/Icon';
import RoleHelper from '../../RoleHelper';
import AchFileDetailListViewer from '../../components/ach/AchFileDetailListViewer';
import AchService from '../../service/AchService';
import moment from 'moment/moment';
import { query } from '@trlogic/client-js';
import { TransactionData } from '../../domain/stream/transaction/TransactionDomains';
import { AchStages } from '../../constant/Constants';
import FolderService from '../../service/FolderService';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import CollectionNames from '../../constant/CollectionNames';
import Order = query.Order;
import Filter = query.Filter;
import QueryBuilder = query.QueryBuilder;
import eq = query.eq;

class AchFileDetailContainer extends Component<Props, State> {
  public state: State = new State();
  private readonly achService: AchService = ServiceContainer.getInstance().get(ServiceNames.ACH);

  public render() {
    if (!RoleHelper.canView('ACH')) {
      return <div>No Permission</div>;
    }

    const { router } = this.props;
    const fileName = router.params.fileName;

    if (!fileName) {
      return <div>File name not found</div>;
    }

    const { firstName, lastName, uploader, createdAt, stage } = this.state;
    const getInitials = (first?: string, last?: string): string => {
      const firstInitial = first && first.length > 0 ? first.substring(0, 1).toUpperCase() : '';
      const lastInitial = last && last.length > 0 ? last.substring(0, 1).toUpperCase() : '';
      return firstInitial + lastInitial || (uploader ? uploader.substring(0, 1).toUpperCase() : '');
    };
    const uppercaseUploader = getInitials(firstName, lastName);
    const fullName = firstName && lastName ? `${firstName} ${lastName}` : uploader || '';

    return (
      <div className={'flex flex-col w-full h-full overflow-y-hidden'}>
        <Breadcrumb />
        <div className={'flex w-full shrink-0 px-5 pt-5 gap-x-5 overflow-x-hidden'}>
          <div className='w-[80px] shrink-0 h-[80px] flex justify-center items-center rounded-[50%] bg-[#EFF2FE]'>
            <Icon icon={'sidebar-ach'} size={30} className={'hidden'} color={'#728EFF'} />
          </div>
          <div className={'flex flex-col gap-y-1 overflow-x-scroll items-start justify-center'}>
            <span className={'text-4xl text-black font-normal'}>{fileName}</span>
            <div className={'flex w-full items-center text-f-black-3 text-xs 2xl:text-md justify-start relative group gap-x-2'}>
              <div className={'flex items-center text-f-black-3 text-xs 2xl:text-md gap-x-2'}>
                <div className={'w-7 h-7 flex items-center justify-center rounded-full bg-[#F4F3FF] border-[0.5px] border-f-gray-3'}>{uppercaseUploader}</div>
                <span className={'text-sm text-gray-500'}>{fullName}</span>
              </div>
              <div className='w-[1px] h-5 bg-f-black-7' />
              <div className={'flex items-center gap-x-2'}>
                {createdAt && (
                  <div className='flex items-center gap-x-2 bg-[#F2F4F7] px-3 h-6 rounded-full text-xs 2xl:text-sm'>
                    <span className={'text-gray-500'}>{moment(createdAt).format('LLL')}</span>
                  </div>
                )}
                {stage && (
                  <div className={`${this.getStatusStyle(stage)} flex px-3 h-6 items-center text-xs 2xl:text-sm justify-center rounded-full`}>
                    {this.formatStage(stage)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className={'flex w-full h-full'}>
          <AchFileDetailListViewer fileName={fileName} onLoadTransactions={this.loadTransactions} />
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    const {
      router: { location, params },
    } = this.props;
    const fileName = params.fileName ? decodeURIComponent(params.fileName) : '';

    if (fileName) {
      await FolderService.calculateAch(fileName, undefined, location.pathname);
    }
    await this.loadAchFileInfo();
  }

  public async componentDidUpdate(prevProps: Readonly<Props>) {
    const {
      router: { location, params },
      page,
    } = this.props;
    const prevFileName = prevProps.router.params.fileName ? decodeURIComponent(prevProps.router.params.fileName) : '';
    const currentFileName = params.fileName ? decodeURIComponent(params.fileName) : '';
    
    if (prevFileName !== currentFileName) {
      if (currentFileName) {
        await FolderService.calculateAch(currentFileName, undefined, location.pathname);
      }
      await this.loadAchFileInfo();
    }
    const prevPagePathLength = prevProps.page.pagePath.length;
    const currentPagePathLength = page.pagePath.length;
    if (prevPagePathLength === 0 && currentPagePathLength > 0 && currentFileName) {
      await FolderService.calculateAch(currentFileName, undefined, location.pathname);
      await this.loadAchFileInfo();
    }
  }

  private loadAchFileInfo = async () => {
    const {
      router: { params },
    } = this.props;
    const fileName = params.fileName ? decodeURIComponent(params.fileName) : '';

    if (!fileName) return;

    try {
      const filter = eq('fileName', fileName);
      const selectQuery = QueryBuilder.select().from(CollectionNames.ACH).where(filter).limit(1).build();

      const achFiles = await this.achService.getAchFiles(selectQuery);
      if (achFiles && achFiles.length > 0) {
        const achFile = achFiles[0];
        this.setState({
          uploader: achFile.uploader,
          firstName: achFile.firstName,
          lastName: achFile.lastName,
          createdAt: achFile.createdAt,
          stage: achFile.stage,
        });
      }
    } catch (error) {
      console.error('Error loading ACH file info:', error);
    }
  };

  private getStatusStyle = (stage: AchStages): string => {
    switch (stage) {
      case 'DONE':
        return 'bg-green-100 text-green-800';
      case 'IN_PROGRESS':
        return 'bg-yellow-100 text-yellow-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      case 'SCHEDULED':
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  private formatStage = (stage: AchStages): string => {
    if (!stage) return stage;
    return stage.charAt(0).toUpperCase() + stage.slice(1).toLowerCase();
  };

  private loadTransactions = async (
    fileName: string,
    rows: number,
    currentPage: number,
    sort: Order[],
    filter: Filter,
  ): Promise<{ data: TransactionData[]; count: number }> => {
    try {
      const combinedFilter = this.achService.buildCombinedFilter(fileName, filter);
      const selectQuery = this.achService.buildTransactionQuery(fileName, currentPage, rows, sort, combinedFilter);

      return await this.achService.getTransactionsByFileName(fileName, selectQuery);
    } catch (error) {
      console.error('Error loading transactions:', error);
      return { data: [], count: 0 };
    }
  };
}

interface Props extends ReduxProps, FormicaProps {}

const mapStateToProps = (state: RootState) => ({
  page: state.page,
});

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

class State {
  uploader?: string;
  firstName?: string;
  lastName?: string;
  createdAt?: number;
  stage?: AchStages;
}

export default withFormica(connector(AchFileDetailContainer));
